/**
 * Historial de ejecuciones de reportes + polling de las que están en curso.
 *
 * El backend responde 202 cuando el reporte supera el umbral síncrono: la
 * ejecución queda persistida y se consulta por el historial. Este hook mantiene
 * esa lista, la pagina con "ver más" y consulta sólo las ejecuciones
 * `PENDIENTE`/`GENERANDO` hasta que todas se resuelven.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ReportFormat,
  ReportRun,
  ReportRunAccepted,
} from '@goelectric/shared-types';
import { reportesApi } from '../api/reportesApi';
import { reportErrorMessage } from '../utils/format';

/** Cantidad de ejecuciones por página del historial. */
export const REPORT_RUNS_PAGE_SIZE = 10;

/** Cadencia del polling mientras haya ejecuciones en curso. */
export const REPORT_RUNS_POLL_MS = 2000;

const ESTADOS_EN_CURSO: ReadonlySet<ReportRun['estado']> = new Set(['PENDIENTE', 'GENERANDO']);

/** Datos mínimos para pintar una ejecución recién aceptada (202) antes del refresco. */
export interface AcceptedRunSeed {
  reporteId: string;
  formato: ReportFormat;
  filtros: Record<string, unknown>;
  columnas: string[];
  entrega?: ReportRun['entrega'];
  destinatarios?: string[];
  asunto?: string | null;
}

export interface UseReportRunsResult {
  runs: ReportRun[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  pollError: string | null;
  refresh: () => void;
  loadMore: () => void;
  registerAccepted: (accepted: ReportRunAccepted, seed: AcceptedRunSeed) => void;
}

function placeholderRun(accepted: ReportRunAccepted, seed: AcceptedRunSeed): ReportRun {
  const now = new Date().toISOString();
  return {
    id: accepted.id,
    reporteId: seed.reporteId,
    formato: seed.formato,
    estado: accepted.estado,
    filtros: seed.filtros,
    columnas: seed.columnas,
    total: null,
    truncado: false,
    contentType: null,
    bytes: null,
    intentos: 0,
    error: null,
    entrega: seed.entrega ?? 'descarga',
    destinatarios: seed.destinatarios ?? [],
    asunto: seed.asunto ?? null,
    enviadoEn: null,
    entregaEstado: null,
    errorEnvio: null,
    createdAt: now,
    updatedAt: now,
  };
}

export function useReportRuns(reportId: string | null): UseReportRunsResult {
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  /** Fuerza a recrear el intervalo tras un refresco manual. */
  const [pollKey, setPollKey] = useState(0);
  /** `true` cuando el polling falló: no se vuelve a intentar solo. */
  const pollStoppedRef = useRef(false);
  /** Descarta respuestas viejas al cambiar de reporte o pedir "ver más". */
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    const seq = ++requestSeqRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setLoadingMore(false);
      setError(null);
    }

    try {
      const page = await reportesApi.listRuns(offset, REPORT_RUNS_PAGE_SIZE);
      if (seq !== requestSeqRef.current) return;
      setRuns((prev) => (append ? [...prev, ...page.data] : page.data));
      setTotal(page.meta.total);
      setHasMore(page.meta.hasMore);
    } catch (caught) {
      if (seq !== requestSeqRef.current) return;
      setError(reportErrorMessage(caught, 'No se pudieron cargar las descargas recientes.'));
    } finally {
      if (seq === requestSeqRef.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    pollStoppedRef.current = false;
    setPollError(null);
    setPollKey((key) => key + 1);
    void fetchPage(0, false);
  }, [fetchPage]);

  // Al cambiar de reporte se reinicia el historial y se detiene el polling anterior.
  useEffect(() => {
    setRuns([]);
    setTotal(0);
    setHasMore(false);
    setError(null);
    setPollError(null);
    pollStoppedRef.current = false;
    if (!reportId) return;
    void fetchPage(0, false);
  }, [reportId, fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchPage(runs.length, true);
  }, [fetchPage, hasMore, loading, loadingMore, runs.length]);

  /** Inserta la ejecución aceptada para que aparezca de inmediato en el panel. */
  const registerAccepted = useCallback((accepted: ReportRunAccepted, seed: AcceptedRunSeed) => {
    setRuns((prev) => {
      if (prev.some((run) => run.id === accepted.id)) return prev;
      return [placeholderRun(accepted, seed), ...prev];
    });
    setTotal((prev) => prev + 1);
  }, []);

  const pendingIds = runs
    .filter((run) => ESTADOS_EN_CURSO.has(run.estado))
    .map((run) => run.id);
  // Clave estable: el efecto se recrea sólo cuando cambia el conjunto de runs en curso.
  const pendingKey = pendingIds.join('|');

  useEffect(() => {
    if (!reportId || !pendingKey || pollStoppedRef.current) return;

    const ids = pendingKey.split('|');
    let cancelled = false;
    let inFlight = false;
    const intervalId = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void (async () => {
        try {
          const updated = await Promise.all(ids.map((id) => reportesApi.getRun(id)));
          if (cancelled) return;
          setRuns((prev) =>
            prev.map((run) => updated.find((candidate) => candidate.id === run.id) ?? run),
          );
        } catch (caught) {
          if (cancelled) return;
          // Honestidad: se corta el polling y se muestra el error; nada de spinners eternos.
          pollStoppedRef.current = true;
          window.clearInterval(intervalId);
          setPollError(
            reportErrorMessage(caught, 'No se pudo actualizar el estado de las descargas.'),
          );
        } finally {
          inFlight = false;
        }
      })();
    }, REPORT_RUNS_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [reportId, pendingKey, pollKey]);

  return {
    runs,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    pollError,
    refresh,
    loadMore,
    registerAccepted,
  };
}
